import React from 'react';
import {
  AdproductsUserBillingGetInvoicesApiHandler_getInvoices,
  AdproductsUserBillingInvoiceApiHandler_getZuoraBillingFilterOptions,
  type AdproductsZuoraBillingInvoiceDto,
} from '@zg-rentals/ts-adproducts-client';
import { LoadingConstants, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';
import { format } from 'date-fns';
import type { AdvancedTableFilterOption } from '@zg-rentals/advanced-table';
import { emitSetFilterEvent } from '../model/ClickstreamEvents';
import { localDateToDate } from '../utils/dateUtils';

export interface InvoiceFilterOption {
  label: string;
  value?: string;
  isChecked: boolean;
}

export interface InvoiceListState {
  invoices: Array<AdproductsZuoraBillingInvoiceDto>;
  offset: number;
  hasMoreData: boolean;
  errorMsg: string | null;
  loadingState: string;
  filters: InvoiceListFilterConfig;
  multiPaymentsEnabled: boolean;
}

const initState = (): InvoiceListState => ({
  invoices: [],
  offset: 0,
  hasMoreData: false,
  errorMsg: null,
  loadingState: LoadingConstants.INITIAL,
  filters: initFilters(),
  multiPaymentsEnabled: false,
});

export interface InvoiceListFilterConfig {
  startDate?: string;
  endDate?: string;
  property: Array<InvoiceFilterOption>;
  city: Array<InvoiceFilterOption>;
  state: Array<InvoiceFilterOption>;
}

const initFilters = (): InvoiceListFilterConfig => ({
  startDate: undefined,
  endDate: undefined,
  property: [],
  city: [],
  state: [],
});

const getFilterItems = (filter: Array<InvoiceFilterOption>) =>
  filter.filter((option) => option.isChecked).map((option) => option.label);

const applyFiltersToRequestDto = (queryParams: InvoiceRequestDto, filters: InvoiceListFilterConfig) => {
  const cityFilters = getFilterItems(filters.city);
  if (cityFilters.length > 0) {
    queryParams.city = cityFilters;
  }
  const stateFilter = getFilterItems(filters.state);
  if (stateFilter.length > 0) {
    queryParams.state = stateFilter;
  }
  const accountFilter = filters.property
    .filter((option: InvoiceFilterOption) => option.isChecked)
    .map((option) => option.value || '');

  if (accountFilter.length > 0) {
    queryParams.accountFilter = accountFilter;
  }
  if (filters.startDate) {
    queryParams.startDate = format(new Date(filters.startDate || ''), 'yyyyMMdd');
  }
  if (filters.endDate) {
    queryParams.endDate = format(new Date(filters.endDate || ''), 'yyyyMMdd');
  }
};

const mapOptions = (options: Array<string>): Array<AdvancedTableFilterOption> => {
  if (!options) return [];
  return options
    .map((option) => ({
      label: option,
      isChecked: false,
    }))
    .sort();
};

const invoiceSort = (a: AdproductsZuoraBillingInvoiceDto, b: AdproductsZuoraBillingInvoiceDto) => {
  const dateDif: number = localDateToDate(b.invoiceLocalDate).valueOf() - localDateToDate(a.invoiceLocalDate).valueOf();
  if (dateDif !== 0) {
    return dateDif;
  }
  const propertyA = a.propertyNames && a.propertyNames[0] ? a.propertyNames[0] : '';
  const propertyB = b.propertyNames && b.propertyNames[0] ? b.propertyNames[0] : '';
  return propertyA.localeCompare(propertyB);
};

export interface InvoiceRequestDto {
  hasPayments: boolean;
  offset: number;
  startDate?: string;
  endDate?: string;
  city?: Array<string>;
  state?: Array<string>;
  accountFilter?: Array<string>;
}

export const useInvoiceList = ({ hasPayments }: { hasPayments: boolean }) => {
  const [state, setState] = React.useState<InvoiceListState>(initState());
  const { user } = useRentalManagerUser();

  const fetchPropertyData = () =>
    AdproductsUserBillingInvoiceApiHandler_getZuoraBillingFilterOptions()
      .then((result) => {
        return setState((newState) => ({
          ...newState,
          filters: {
            ...newState.filters,
            property: result.accountOptions.map((option) => ({
              label: option.accountName,
              isChecked: false,
              value: option.accountNumber,
            })),
            city: mapOptions(result.cities),
            state: mapOptions(result.states),
          },
        }));
      })
      .catch((error) => {
        logError({ error, errorType: 'BillingPortalLoadOptions' });
      });

  const fetchBillingData = (requestDto?: InvoiceRequestDto) => {
    if (!requestDto) {
      requestDto = {
        hasPayments,
        offset: state.offset,
      };
      applyFiltersToRequestDto(requestDto, state.filters);
    }
    setState((lastState) => ({
      ...lastState,
      loadingState: LoadingConstants.LOADING,
    }));
    AdproductsUserBillingGetInvoicesApiHandler_getInvoices(requestDto)
      .then((result) => {
        return setState((lastState) => {
          // don't allow non sequential loads
          if (result.offset + 1 <= lastState.offset) return lastState;
          const invoiceData = result;
          const newInvoices = invoiceData.invoiceList ? invoiceData.invoiceList : [];
          newInvoices.sort(invoiceSort);
          return {
            ...lastState,
            invoices: lastState.invoices.concat(newInvoices),
            offset: invoiceData.offset + 1,
            hasMoreData: invoiceData.hasMoreData || false,
            multiPaymentsEnabled: invoiceData.multiPaymentsEnabled,
            loadingState: LoadingConstants.SUCCESS,
          };
        });
      })
      .catch(() => {
        setState((lastState) => ({
          ...lastState,
          loadingState: LoadingConstants.ERROR,
        }));
      });
  };

  const setFilters = (newFilters: InvoiceListFilterConfig) => {
    emitSetFilterEvent({ user });
    setState({
      ...state,
      filters: newFilters,
      invoices: [],
      offset: 0,
      hasMoreData: false,
    });
    const requestDto: InvoiceRequestDto = {
      hasPayments,
      offset: 0,
    };
    applyFiltersToRequestDto(requestDto, newFilters);
    // Call fetch billing data with new filters to override current state
    fetchBillingData(requestDto);
  };

  const refreshInvoices = () => {
    setState(initState());
  };

  return {
    ...state,
    fetchBillingData,
    fetchPropertyData,
    setFilters,
    refreshInvoices,
  };
};
